import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { changeBusiness } from '~/actions';
import { putBusiness } from '~/api';
import BillingBanner from '~/components/BillingBanner';
import Button from '~/components/Button';
import DropdownButton from '~/components/DropdownButton';
import DropdownInput from '~/components/DropdownInput';
import Gap from '~/components/Gap';
import Header from '~/components/Header';
import MainContainer from '~/components/MainContainer';
import { MobileCtaContext } from '~/components/SidebarLayout';
import Text from '~/components/Text';
import { ADD_ESTIMATE_ROUTE, ADD_INVOICE_ROUTE, BILLING_ROUTE } from '~/constants/routes';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { useUserLimits } from '~/hooks/useUserLimits';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';
import { useDispatch, useSelector } from '~/store';
import { selectUser } from '~/store/slices/formSlice/selectors';
import { IBusiness } from '~/types/business';
import { CURRENCY_LIST } from '~/utils/amount';
import { apiResponseHandler } from '~/utils/api';
import AddClientModal from '../Client/AddClient/AddClientModal';
import { useClientList } from '../Client/hooks/useClientList';
import AnalyticsChart from './AnalyticsChart';
import InvoiceSummary from './InvoiceSummary';
import RecentInvoices from './RecentInvoices';
import * as Styles from './styles';

const Dashboard = () => {
  const user = useSelector(selectUser);
  const currency = user.businessDetails.currency;
  const business = user.businessDetails;
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { canCreateClient, canCreateInvoice, canCreateEstimate } = useUserLimits();

  const handleAddInvoice = () => {
    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: 'Dashboard',
      button_name: 'New Invoice',
      button_id: 'new_invoice',
      section: 'Header',
    });

    if (canCreateInvoice) {
      navigate(ADD_INVOICE_ROUTE);
    } else {
      navigate(`${BILLING_ROUTE}?ref=invoices`);
    }
  };

  const handleAddEstimate = () => {
    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: 'Dashboard',
      button_name: 'New Estimate',
      button_id: 'new_estimate',
      section: 'Header',
    });

    if (canCreateEstimate) {
      navigate(ADD_ESTIMATE_ROUTE);
    } else {
      navigate(`${BILLING_ROUTE}?ref=invoices`);
    }
  };

  const handleAddClient = () => {
    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: 'Dashboard',
      button_name: 'New Client',
      button_id: 'new_client',
      section: 'Header',
    });

    if (canCreateClient) {
      modalActions.toggle();
    } else {
      navigate(`${BILLING_ROUTE}?ref=clients`);
    }
  };

  const setCurrency = (newCurrency: string) => {
    apiResponseHandler<IBusiness>(
      putBusiness(business.business_uuid, { ...business, currency: newCurrency }),
    );
    dispatch(changeBusiness({ currency: newCurrency }));
  };
  const { modalActions, isModalOpen } = useClientList(false);

  const isDesktop = useIsDesktop();

  const { setMobileCta } = useContext(MobileCtaContext);

  useEffect(() => {
    setMobileCta(
      <>
        <Button variant="primary" className="full-flex" size="sm" onClick={handleAddClient}>
          + New Client
        </Button>
        <Button variant="primary" size="sm" className="full-flex" onClick={handleAddInvoice}>
          + New Invoice
        </Button>
      </>,
    );

    if (!currency) {
      setCurrency('USD');
    }

    return () => setMobileCta(null);
  }, [canCreateClient, canCreateInvoice, navigate]);

  return (
    <div>
      <Header
        headerText="Dashboard"
        rightJsx={
          isDesktop ? (
            <DropdownButton
              buttonText="+ Create New"
              variant="primary"
              size="smd"
              options={[
                {
                  label: 'Invoice',
                  onClick: handleAddInvoice,
                },
                {
                  label: 'Estimate',
                  onClick: handleAddEstimate,
                },
                {
                  label: 'Client',
                  onClick: handleAddClient,
                },
              ]}
            />
          ) : null
        }
      />

      <AddClientModal
        isModalOpen={isModalOpen}
        onCloseClientModal={() => {
          modalActions.off();
        }}
      />

      <MainContainer>
        {(!canCreateClient || !canCreateInvoice || !canCreateEstimate) && (
          <BillingBanner
            variant="error"
            title="Limit Reached. Ready to Grow? 🚀"
            description="You've reached your usage limit on the Free Plan. Upgrade now to unlock unlimited access and continue growing your business."
            buttonText="Upgrade now"
            onButtonClick={() => {
              analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
                screen_name: 'Dashboard',
                link_name: 'Upgrade now',
                link_id: 'upgrade_banner',
                section: 'Banner',
              });
              navigate(`${BILLING_ROUTE}?ref=dashboard`);
            }}
          />
        )}

        <Styles.HeaderContent className="full-width">
          <Text size={isDesktop ? 'md' : 'smd'} className="bold">
            Welcome Back
          </Text>

          <Gap l={8} />

          <div>
            {isDesktop && (
              <>
                <Text size="xs">Currency</Text>
                <Gap t={4} />
              </>
            )}
            <DropdownInput
              value={currency}
              className={isDesktop ? 'filter-dd' : 'mini-filter-dd'}
              options={CURRENCY_LIST}
              onChange={setCurrency}
              search={false}
              isMini={!isDesktop}
            />
          </div>
        </Styles.HeaderContent>
        <Gap t={30} />
        <InvoiceSummary />
        <Gap t={32} />
        <AnalyticsChart />
        <Gap t={32} />
        <RecentInvoices />
      </MainContainer>
    </div>
  );
};

export default Dashboard;
